import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PenLine, Star, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function Stars({ value, onSelect, size = "w-4 h-4" }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(i)}
          className={onSelect ? "hover:scale-125 transition-transform" : "cursor-default"}
          data-testid={onSelect ? `review-star-${i}` : undefined}
        >
          <Star className={`${size} ${i <= value ? "fill-gold text-gold" : "text-ink/20"}`} />
        </button>
      ))}
    </span>
  );
}

export default function ReviewsSection() {
  const [data, setData] = useState({ reviews: [], average: null, count: 0, breakdown: {} });
  const [products, setProducts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", name: "", city: "", kid_age: "", rating: 5, comment: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/reviews").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/products").then((r) => {
      setProducts(r.data.products);
      if (r.data.products[0]) setForm((f) => ({ ...f, product_id: r.data.products[0].id }));
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.comment.trim()) {
      toast.error("Please add your name and a few words");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/products/${form.product_id}/reviews`, form);
      toast.success("Thank you! Your review is live.");
      setDrawerOpen(false);
      setForm((f) => ({ ...f, name: "", city: "", kid_age: "", comment: "", rating: 5 }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm outline-none focus:border-terracotta transition-colors";

  return (
    <section id="reviews" className="py-20 md:py-28 bg-blush/50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-sage-dark mb-4">Verified Parent Reviews</p>
            <h2 className="font-serif font-black text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight leading-[1.1]">
              Loved in living rooms<br />from Jaipur to Kochi.
            </h2>
          </div>
          <button
            onClick={() => setDrawerOpen(true)}
            className="rounded-full bg-ink text-cream px-6 py-3.5 text-sm font-semibold hover:bg-terracotta transition-colors duration-300 flex items-center gap-2"
            data-testid="write-review-btn"
          >
            <PenLine className="w-4 h-4" /> Write a Review
          </button>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-[2rem] p-8 flex flex-col justify-center"
            data-testid="reviews-summary"
          >
            <p className="font-serif font-black text-6xl text-ink">{data.average ?? "—"}</p>
            <Stars value={Math.round(data.average || 0)} size="w-5 h-5" />
            <p className="font-mono text-xs text-charcoal mt-2">{data.count} verified reviews</p>
            <div className="mt-5 space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const n = data.breakdown?.[String(star)] || 0;
                const pct = data.count ? (n / data.count) * 100 : 0;
                return (
                  <div key={star} className="flex items-center gap-2">
                    <span className="font-mono text-[10px] w-3">{star}</span>
                    <div className="flex-1 h-1.5 bg-blush rounded-full overflow-hidden">
                      <div className="h-full bg-gold rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="font-mono text-[10px] text-charcoal/60 w-6 text-right">{n}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {data.reviews.slice(0, 6).map((r, i) => (
              <motion.article
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white rounded-3xl p-6"
                data-testid={`review-card-${r.id}`}
              >
                <Stars value={r.rating} />
                <p className="text-sm text-charcoal leading-relaxed mt-3 line-clamp-4">"{r.comment}"</p>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-ink/5">
                  <div>
                    <p className="text-xs font-semibold text-ink">{r.name} · {r.city}</p>
                    <p className="font-mono text-[10px] text-charcoal/60 mt-0.5">{r.product_name}</p>
                  </div>
                  {r.kid_age && (
                    <span className="font-mono text-[10px] bg-mint text-sage-dark rounded-full px-2.5 py-1">kid: {r.kid_age}</span>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed right-0 top-0 bottom-0 z-[90] w-full max-w-md bg-cream p-6 overflow-y-auto shadow-2xl"
              data-testid="review-drawer"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif font-bold text-2xl text-ink">Share your story</h3>
                <button onClick={() => setDrawerOpen(false)} className="rounded-full p-2 hover:bg-blush" data-testid="review-drawer-close" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <select className={inputCls} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="review-product-select">
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] tracking-widest uppercase text-charcoal/70">Rating</span>
                  <Stars value={form.rating} onSelect={(v) => setForm({ ...form, rating: v })} size="w-6 h-6" />
                </div>
                <input className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" />
                <div className="grid grid-cols-2 gap-4">
                  <input className={inputCls} placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="review-city-input" />
                  <input className={inputCls} placeholder="Kid's age (e.g. 5Y)" value={form.kid_age} onChange={(e) => setForm({ ...form, kid_age: e.target.value })} data-testid="review-kid-age-input" />
                </div>
                <textarea
                  className={`${inputCls} min-h-28 resize-none`}
                  placeholder="How did the dress hold up to twirls, washes and weddings? *"
                  value={form.comment}
                  onChange={(e) => setForm({ ...form, comment: e.target.value })}
                  data-testid="review-comment-input"
                />
                <button
                  onClick={submit}
                  disabled={saving}
                  className="w-full rounded-full bg-terracotta text-white py-4 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-50"
                  data-testid="review-submit-btn"
                >
                  {saving ? "Publishing..." : "Publish Review"}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </section>
  );
}
